// PointCheckMenu.jsx
import React, { useState, useEffect, useRef } from 'react';
import './PointCheck.css';
import { useNavigate } from 'react-router-dom';
import en from '../locales/eng.json';
import th from '../locales/th.json';
import CONFIG from '../config';
import jsQR from 'jsqr';

function PointCheckMenu() {
  const navigate = useNavigate();

  // Localization
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'TH';
  });
  const [t, setT] = useState(language === 'EN' ? en : th);

  // Admin login states
  const [showLogin, setShowLogin] = useState(false);
  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');

  // QR Scanner visibility
  const [showScanner, setShowScanner] = useState(false);

  // Manual input field
  const [manualCouponCode, setManualCouponCode] = useState('');

  // Video / canvas refs
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const requestRef = useRef();

  // On mount: check if admin is logged in
  useEffect(() => {
    const adminData = localStorage.getItem('adminData');
    if (!adminData) {
      setShowLogin(true);
    }
  }, []);

  // Update localization when language changes
  useEffect(() => {
    localStorage.setItem('language', language);
    setT(language === 'EN' ? en : th);
  }, [language]);

  // ---- Admin Login Handlers ----
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoginError('');

    try {
      const res = await fetch(`${CONFIG.API_BASE_URL}/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: loginUsername,
          password: loginPassword,
        }),
      });

      if (!res.ok) {
        if (res.status === 400 || res.status === 404) {
          setLoginError('Invalid username or password');
        } else {
          setLoginError('Server error. Please try again.');
        }
        return;
      }

      const data = await res.json();
      localStorage.setItem('adminData', JSON.stringify(data));
      setShowLogin(false);
    } catch (err) {
      console.error('Login error:', err);
      setLoginError('Network error. Please try again.');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminData');
    setShowLogin(true);
  };

  // ---- Start / Stop camera + scan loop ----
  useEffect(() => {
    let videoStream = null;

    async function startCameraAndScan() {
      try {
        // 1) Ask for camera access
        videoStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
        });
        if (videoRef.current) {
          videoRef.current.srcObject = videoStream;
          videoRef.current.setAttribute('playsinline', true); // required for iOS safari
          await videoRef.current.play();
          scanFrame();
        }
      } catch (err) {
        console.error('getUserMedia error:', err);
        // You could show an alert to the user here
      }
    }

    function scanFrame() {
      if (
        !videoRef.current ||
        videoRef.current.readyState !== videoRef.current.HAVE_ENOUGH_DATA
      ) {
        requestRef.current = requestAnimationFrame(scanFrame);
        return;
      }

      const video = videoRef.current;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(
        imageData.data,
        imageData.width,
        imageData.height,
        { inversionAttempts: 'dontInvert' }
      );

      if (code && code.data) {
        // Found a QR code! Stop scanning, navigate
        setShowScanner(false);
        if (videoStream) {
          videoStream.getTracks().forEach((track) => track.stop());
        }
        window.location.href = code.data;
        return;
      }

      requestRef.current = requestAnimationFrame(scanFrame);
    }

    if (showScanner) {
      startCameraAndScan();
    } else {
      // If scanner is hidden, stop video & cancel loop
      if (videoStream) {
        videoStream.getTracks().forEach((track) => track.stop());
      }
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
    }

    // Cleanup on unmount
    return () => {
      if (videoStream) {
        videoStream.getTracks().forEach((track) => track.stop());
      }
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
    };
  }, [showScanner]);

  // ---- Manual “Go” button ----
const handleManualGo = () => {
  // 1. Trim whitespace, then drop anything after the first comma:
  const raw = manualCouponCode.trim();
  const beforeComma = raw.split(',')[0];

  if (!beforeComma) {
    alert('Please enter a valid coupon code (e.g. UF6CID4)');
    return;
  }

  // 2. Match only the UF{digits}CID{digits} portion:
  const match = beforeComma.match(/^UF(\d+)CID(\d+)$/i);
  if (!match) {
    alert('Format must be UF{coupon_id}CID{user_coupon_id}, e.g. UF1CID1,100');
    return;
  }

  const coupon_id = match[1];
  const user_coupon_id = match[2];

  navigate(
    `/point-check?coupon_id=${user_coupon_id}&user_coupon_id=${coupon_id}`
  );
};


  // ---- Show login modal if needed ----
  if (showLogin) {
    return (
      <div className="login-modal-overlay">
        <div className="login-modal">
          <h2>Admin Login</h2>
          <form onSubmit={handleLoginSubmit}>
            <div className="form-group">
              <label>Username</label>
              <input
                type="text"
                value={loginUsername}
                onChange={(e) => setLoginUsername(e.target.value)}
                required
                autoFocus
              />
            </div>
            <div className="form-group">
              <label>Password</label>
              <input
                type="password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                required
              />
            </div>
            {loginError && <div className="login-error">{loginError}</div>}
            <button type="submit" className="login-button">
              Log In
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ---- Main render ----
  return (
    <div style={{ backgroundColor: '#FFFFFF', minHeight: '100vh' }}>
      {/* Header */}
      <div className="point-header-gradient">
        <img
          onClick={() => navigate('/admin')}
          src="/assets/logo-ujoy.png"
          alt="logo"
          className="logo-ujoy"
        />
        <div className="right-section">
          <div className="lang-selector">
            <button
              className={`lang-button ${language === 'TH' ? 'active' : ''}`}
              onClick={() => setLanguage('TH')}
            >
              TH
            </button>
            |
            <button
              className={`lang-button ${language === 'EN' ? 'active' : ''}`}
              onClick={() => setLanguage('EN')}
            >
              EN
            </button>
          </div>
          {localStorage.getItem('adminData') && (
            <button
              onClick={handleLogout}
              className="logout-button"
              style={{
                marginLeft: '1rem',
                backgroundColor: '#e74c3c',
                color: 'white',
                border: 'none',
                padding: '4px 8px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.875rem',
              }}
            >
              Logout
            </button>
          )}
        </div>
      </div>

      <div className="point-header-bar">
        <span className="point-header-title">{t.pointcheck.pointcheck}</span>
      </div>

      {/* === Buttons / Inputs to scan or manually enter coupon code === */}
      <div
        className="scan-manual-section"
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '16px',
        }}
      >
        <button
          onClick={() => setShowScanner(true)}
          className="scan-button"
          style={{
            backgroundColor: '#079AAE',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '1rem',
            marginRight: '12px',
          }}
        >
          {t.pointcheck.scan_qr || 'Scan QR'}
        </button>

        <input
          type="text"
          placeholder={t.pointcheck.enter_coupon_code || 'Enter Coupon Code (e.g. UF6CID4)'}
          value={manualCouponCode}
          onChange={(e) => setManualCouponCode(e.target.value)}
          style={{
            padding: '8px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            width: '220px',
            marginRight: '8px',
            fontSize: '1rem',
          }}
        />
        <button
          onClick={handleManualGo}
          className="go-button"
          style={{
            backgroundColor: '#27AE60',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '1rem',
          }}
        >
          {t.pointcheck.go || 'Go'}
        </button>
      </div>

      {/* Full-screen QR Scanner Overlay */}
      {showScanner && (
        <div
          className="qr-scanner-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.85)',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <button
            onClick={() => setShowScanner(false)}
            className="qr-close-button"
            style={{
              background: 'none',
              border: 'none',
              color: 'white',
              fontSize: '1.5rem',
              alignSelf: 'flex-end',
              margin: '16px',
              cursor: 'pointer',
            }}
          >
            ×
          </button>
          <div style={{ flex: 1, position: 'relative' }}>
            <video
              ref={videoRef}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
            <canvas
              ref={canvasRef}
              style={{
                display: 'none',
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default PointCheckMenu;
